import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

const ABBR_RE = /^[A-Z0-9]{1,10}$/;

function StatusPill({ s }) {
  return <Badge variant={s === "ACTIVE" ? "default" : "secondary"}>{s === "ACTIVE" ? "Aktif" : "Nonaktif"}</Badge>;
}

function StatusSelect({ value, onChange, testid }) {
  return <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value="ACTIVE">Aktif</SelectItem><SelectItem value="INACTIVE">Nonaktif</SelectItem></SelectContent>
  </Select>;
}

function validate(form) {
  if (!form.name?.trim()) return "Nama wajib diisi.";
  if (!ABBR_RE.test((form.abbreviation || "").trim().toUpperCase())) return "Singkatan wajib 1–10 huruf besar/angka tanpa spasi atau simbol.";
  return null;
}

function CheckList({ items, selected, onToggle, prefix, search }) {
  const shown = items.filter((x) => !search || `${x.code} ${x.name}`.toLowerCase().includes(search.toLowerCase()));
  return <div className="max-h-44 overflow-auto border rounded p-2 space-y-1">
    {shown.length === 0 && <div className="text-xs text-slate-400">Tidak ada data.</div>}
    {shown.map((x) => <label key={x.id} className="flex items-center gap-2 text-sm cursor-pointer">
      <Checkbox checked={selected.includes(x.id)} onCheckedChange={() => onToggle(x.id)} data-testid={`${prefix}-${x.code || x.id}`} />
      <span className="font-mono text-xs text-slate-500">{x.code}</span><span>{x.name}</span>
    </label>)}
  </div>;
}

function DocTypeDialog({ form, setForm, workflows, onSave }) {
  if (!form) return null;
  const toggle = (k) => setForm({ ...form, txn_keys: form.txn_keys.includes(k) ? form.txn_keys.filter((x) => x !== k) : [...form.txn_keys, k] });
  return <Dialog open onOpenChange={() => setForm(null)}>
    <DialogContent data-testid="doctype-dialog"><DialogHeader><DialogTitle>{form.id ? "Ubah" : "Tambah"} Jenis Dokumen</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="doctype-name-input" /></div>
        <div><Label>Singkatan</Label><Input value={form.abbreviation} onChange={(e) => setForm({ ...form, abbreviation: e.target.value.toUpperCase() })} data-testid="doctype-abbr-input" /></div>
        <div><Label>Workflow yang dipetakan (penomoran otomatis)</Label>
          <CheckList items={workflows.map((w) => ({ id: w.key, code: "", name: w.label }))} selected={form.txn_keys} onToggle={toggle} prefix="doctype-wf" />
          <p className="text-xs text-slate-500 mt-1">Tanpa workflow: jenis hanya terdaftar, tidak dipakai otomatis.</p></div>
        <div><Label>Status</Label><StatusSelect value={form.status} onChange={(v) => setForm({ ...form, status: v })} testid="doctype-status-select" /></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Batal</Button><Button onClick={onSave} data-testid="doctype-save-btn">Simpan</Button></DialogFooter>
    </DialogContent>
  </Dialog>;
}

function PartyDialog({ form, setForm, customers, suppliers, onSave }) {
  const [q, setQ] = useState("");
  const formId = form?.id;
  useEffect(() => { setQ(""); }, [formId, form === null]);
  if (!form) return null;
  const toggle = (field) => (id) => setForm({ ...form, [field]: form[field].includes(id) ? form[field].filter((x) => x !== id) : [...form[field], id] });
  return <Dialog open onOpenChange={() => setForm(null)}>
    <DialogContent className="max-w-2xl" data-testid="party-dialog"><DialogHeader><DialogTitle>{form.id ? "Ubah" : "Tambah"} Singkatan Pihak</DialogTitle></DialogHeader>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Nama toko/perusahaan</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="party-name-input" /></div>
        <div><Label>Singkatan</Label><Input value={form.abbreviation} onChange={(e) => setForm({ ...form, abbreviation: e.target.value.toUpperCase() })} data-testid="party-abbr-input" /></div>
        <div className="sm:col-span-2"><Label>Cari pelanggan/pemasok</Label><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kode atau nama" data-testid="party-search-input" /></div>
        <div><Label>Pelanggan tertaut</Label><CheckList items={customers} selected={form.customer_ids} onToggle={toggle("customer_ids")} prefix="party-cus" search={q} /></div>
        <div><Label>Pemasok tertaut</Label><CheckList items={suppliers} selected={form.supplier_ids} onToggle={toggle("supplier_ids")} prefix="party-sup" search={q} /></div>
        <div><Label>Status</Label><StatusSelect value={form.status} onChange={(v) => setForm({ ...form, status: v })} testid="party-status-select" /></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Batal</Button><Button onClick={onSave} data-testid="party-save-btn">Simpan</Button></DialogFooter>
    </DialogContent>
  </Dialog>;
}

function PreviewCard({ workflows, customers, suppliers }) {
  const [p, setP] = useState({ txn_key: "sales_invoice", party_code: "", date: new Date().toISOString().slice(0, 10) });
  const [res, setRes] = useState(null);
  const kind = workflows.find((w) => w.key === p.txn_key)?.party_kind;
  const parties = kind === "customer" ? customers : kind === "supplier" ? suppliers : [];
  const run = async () => {
    try {
      const { data } = await api.get("/numbering/preview", { params: { ...p, party_code: p.party_code || undefined } });
      setRes({ ok: true, text: data.number });
    } catch (e) { setRes({ ok: false, text: formatApiError(e) }); }
  };
  return <Card><CardHeader><CardTitle className="text-lg">Pratinjau Nomor Berikutnya</CardTitle></CardHeader>
    <CardContent className="grid sm:grid-cols-4 gap-3 items-end">
      <div><Label>Workflow</Label><Select value={p.txn_key} onValueChange={(v) => setP({ ...p, txn_key: v, party_code: "" })}>
        <SelectTrigger data-testid="preview-workflow-select"><SelectValue /></SelectTrigger>
        <SelectContent>{workflows.map((w) => <SelectItem key={w.key} value={w.key}>{w.label}</SelectItem>)}</SelectContent></Select></div>
      <div><Label>Pihak</Label>{kind ? <Select value={p.party_code} onValueChange={(v) => setP({ ...p, party_code: v })}>
        <SelectTrigger data-testid="preview-party-select"><SelectValue placeholder="Pilih pihak" /></SelectTrigger>
        <SelectContent>{parties.filter((x) => x.code).map((x) => <SelectItem key={x.id} value={x.code}>{x.code} — {x.name}</SelectItem>)}</SelectContent></Select>
        : <div className="text-sm text-slate-500 py-2">Internal (kode perusahaan)</div>}</div>
      <div><Label>Tanggal dokumen</Label><Input type="date" value={p.date} onChange={(e) => setP({ ...p, date: e.target.value })} data-testid="preview-date-input" /></div>
      <Button onClick={run} data-testid="preview-run-btn">Pratinjau</Button>
      {res && <div className={`sm:col-span-4 font-mono text-lg ${res.ok ? "text-slate-900" : "text-rose-600 text-sm"}`} data-testid="preview-result">{res.text}</div>}
      <p className="sm:col-span-4 text-xs text-slate-500">Pratinjau tidak memakai nomor. Nomor final diberikan server saat dokumen disimpan.</p>
    </CardContent></Card>;
}

function CompanyCodeCard({ value, onSaved }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  const save = async () => {
    if (!ABBR_RE.test(v)) { toast.error("Kode perusahaan wajib 1–10 huruf besar/angka."); return; }
    try { await api.put("/numbering/settings", { company_code: v }); toast.success("Kode perusahaan disimpan."); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return <Card><CardHeader><CardTitle className="text-lg">Kode Perusahaan (dokumen internal tanpa pihak)</CardTitle></CardHeader>
    <CardContent className="flex gap-3 items-end">
      <div><Label>Kode</Label><Input value={v} onChange={(e) => setV(e.target.value.toUpperCase())} className="w-40" data-testid="company-code-input" /></div>
      <Button onClick={save} data-testid="company-code-save-btn">Simpan</Button>
      <p className="text-xs text-slate-500">Dipakai Beban & Transfer Bank, contoh 00001/EXP-{v || "ABS"}/X/2026.</p>
    </CardContent></Card>;
}

export default function NumberingPage() {
  const [meta, setMeta] = useState({ workflows: [], company_code: "" });
  const [types, setTypes] = useState([]);
  const [abbrs, setAbbrs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [typeForm, setTypeForm] = useState(null);
  const [partyForm, setPartyForm] = useState(null);
  const load = async () => {
    try {
      const [m, t, a, c, s] = await Promise.all([api.get("/numbering/meta"), api.get("/numbering/doc-types"),
        api.get("/numbering/party-abbreviations"), api.get("/masters/customers"), api.get("/masters/suppliers")]);
      setMeta(m.data); setTypes(t.data); setAbbrs(a.data); setCustomers(c.data); setSuppliers(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  const wfLabel = useMemo(() => Object.fromEntries(meta.workflows.map((w) => [w.key, w.label])), [meta]);
  const nameOf = (list, ids) => ids.map((i) => list.find((x) => x.id === i)?.name || i).join(", ");
  const save = async (form, setForm, url) => {
    const err = validate(form);
    if (err) { toast.error(err); return; }
    const body = { ...form, name: form.name.trim(), abbreviation: form.abbreviation.trim().toUpperCase() };
    try {
      if (form.id) await api.put(`${url}/${form.id}`, body); else await api.post(url, body);
      toast.success("Data penomoran disimpan."); setForm(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return <div className="space-y-6 page-fade" data-testid="numbering-page">
    <div><h1 className="font-display text-3xl sm:text-4xl font-bold">Format Nomor Transaksi</h1>
      <p className="text-sm text-slate-500 mt-1">Format: 00001/JENIS-PIHAK/BULAN ROMAWI/TAHUN. Urutan per jenis dokumen + pihak + tahun, mulai 00001 tiap tahun. Mengubah singkatan tidak menomori ulang dokumen lama.</p></div>
    <CompanyCodeCard value={meta.company_code} onSaved={load} />
    <Card><CardHeader className="flex flex-row items-center justify-between"><CardTitle className="text-lg">Jenis Dokumen</CardTitle>
      <Button size="sm" onClick={() => setTypeForm({ name: "", abbreviation: "", txn_keys: [], status: "ACTIVE" })} data-testid="doctype-add-btn"><Plus className="w-4 h-4 mr-1" />Tambah</Button></CardHeader>
      <CardContent className="p-0"><Table><TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Singkatan</TableHead><TableHead>Workflow</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
        <TableBody>{types.map((t) => <TableRow key={t.id} data-testid={`doctype-row-${t.abbreviation}`}>
          <TableCell>{t.name}</TableCell><TableCell className="font-mono">{t.abbreviation}</TableCell>
          <TableCell className="text-sm">{t.txn_keys.length ? t.txn_keys.map((k) => wfLabel[k] || k).join(", ") : <span className="text-amber-600">Belum ada workflow di aplikasi — tidak dinomori otomatis</span>}</TableCell>
          <TableCell><StatusPill s={t.status} /></TableCell>
          <TableCell><Button size="sm" variant="ghost" onClick={() => setTypeForm({ ...t })} data-testid={`doctype-edit-${t.abbreviation}`}><Pencil className="w-4 h-4" /></Button></TableCell>
        </TableRow>)}</TableBody></Table></CardContent></Card>
    <Card><CardHeader className="flex flex-row items-center justify-between"><CardTitle className="text-lg">Singkatan Toko / Perusahaan</CardTitle>
      <Button size="sm" onClick={() => setPartyForm({ name: "", abbreviation: "", customer_ids: [], supplier_ids: [], status: "ACTIVE" })} data-testid="party-add-btn"><Plus className="w-4 h-4 mr-1" />Tambah</Button></CardHeader>
      <CardContent className="p-0"><Table><TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Singkatan</TableHead><TableHead>Pelanggan</TableHead><TableHead>Pemasok</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
        <TableBody>{abbrs.map((a) => <TableRow key={a.id} data-testid={`party-row-${a.abbreviation}`}>
          <TableCell>{a.name}</TableCell><TableCell className="font-mono">{a.abbreviation}</TableCell>
          <TableCell className="text-sm">{a.customer_ids.length ? nameOf(customers, a.customer_ids) : a.supplier_ids.length ? "-" : <span className="text-amber-600">Belum tertaut</span>}</TableCell>
          <TableCell className="text-sm">{nameOf(suppliers, a.supplier_ids) || "-"}</TableCell>
          <TableCell><StatusPill s={a.status} /></TableCell>
          <TableCell><Button size="sm" variant="ghost" onClick={() => setPartyForm({ ...a })} data-testid={`party-edit-${a.abbreviation}`}><Pencil className="w-4 h-4" /></Button></TableCell>
        </TableRow>)}</TableBody></Table></CardContent></Card>
    <PreviewCard workflows={meta.workflows} customers={customers} suppliers={suppliers} />
    <DocTypeDialog form={typeForm} setForm={setTypeForm} workflows={meta.workflows} onSave={() => save(typeForm, setTypeForm, "/numbering/doc-types")} />
    <PartyDialog form={partyForm} setForm={setPartyForm} customers={customers} suppliers={suppliers} onSave={() => save(partyForm, setPartyForm, "/numbering/party-abbreviations")} />
  </div>;
}
